import React, { type ComponentPropsWithoutRef, useState } from 'react';
import { Router, type Plugin, MemoryRouter } from 'react-resource-router';
import {
	createResourcesPlugin,
	type ResourceStoreContext,
	type ResourceStoreData,
} from 'react-resource-router/resources';
import { createEntryPointsPlugin } from '@atlassian/jira-entry-points-plugin';
import RelayEnvironment from '@atlassian/jira-relay-environment';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

const usePlugins = ({
	resourceContext,
	resourceData,
}: {
	resourceContext?: ResourceStoreContext | undefined;
	resourceData?: ResourceStoreData | undefined;
}) => {
	const tenantContext = useTenantContext();
	// make sure "plugins" are created only once, ignore prop change/rerender
	const [pluginsWithOverride] = useState(() => {
		const jiraPlugins: Plugin[] = [];

		const resourcesPlugin = createResourcesPlugin({
			context: resourceContext,
			resourceData,
		});

		jiraPlugins.push(resourcesPlugin);

		jiraPlugins.push(
			createEntryPointsPlugin({
				environment: RelayEnvironment,
				entryPointParams: { tenantContext },
			}),
		);

		return jiraPlugins;
	});

	return pluginsWithOverride;
};

type Props = Omit<ComponentPropsWithoutRef<typeof Router>, 'plugins'> & {
	resourceContext?: ResourceStoreContext | undefined;
	resourceData?: ResourceStoreData | undefined;
};

export const JiraRouter = ({ children, ...props }: Props) => {
	const pluginsWithOverride = usePlugins({
		resourceContext: props.resourceContext,
		resourceData: props.resourceData,
	});

	return (
		<Router plugins={pluginsWithOverride} {...props}>
			{children}
		</Router>
	);
};

type MemoryRouterProps = Omit<ComponentPropsWithoutRef<typeof MemoryRouter>, 'plugins'> & {
	resourceContext?: ResourceStoreContext | undefined;
	resourceData?: ResourceStoreData | undefined;
};

export const JiraMemoryRouter = ({
	children,
	resourceData,
	resourceContext,
	...props
}: MemoryRouterProps) => {
	const pluginsWithOverride = usePlugins({
		resourceContext,
		resourceData,
	});

	return (
		<MemoryRouter plugins={pluginsWithOverride} {...props}>
			{children}
		</MemoryRouter>
	);
};
